import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Accordion } from "react-bootstrap";
import {
    getNotes,
    createNote,
    sendNoteNotifications,
} from "../services/APIClient";
import "./NoteModal.css";

function NoteModal({
    entityName,
    elementtype,
    elementid,
    noteType,
    teacherid,
    teachers,
    onSuccess,
    filterByNoteType = false,
}) {
    const getModalTitle = () => {
        if (elementtype === "student") return `Add Note to Student, ${entityName || ""}`;
        if (elementtype === "class") return `Add Note to Class ${entityName || ""}`;
        return "Add Note";
    };
    const [show, setShow] = useState(false);
    const [noteAdded, setNoteAdded] = useState(false);
    const [note, setNote] = useState("");
    const [existingNotes, setExistingNotes] = useState([]);
    const [hasExistingNotes, setHasExistingNotes] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);
    const [selectedTeachers, setSelectedTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingNotes, setFetchingNotes] = useState(false);

    // Fetch on mount to determine icon state
    useEffect(() => {
        if (elementtype && elementid) {
            getNotes(elementtype, elementid, filterByNoteType ? noteType : null)
                .then((response) => {
                    setHasExistingNotes((response.data || []).length > 0);
                })
                .catch(() => {});
        }
    }, [elementtype, elementid, noteType]);

    useEffect(() => {
        if (show && elementtype && elementid) {
            fetchNotes();
        }
    }, [show, elementtype, elementid]);

    useEffect(() => {
        if (!show) {
            setNote("");
            setSendEmail(false);
            setSelectedTeachers([]);
            setExistingNotes([]);
        }
    }, [show]);

    const fetchNotes = async () => {
        setFetchingNotes(true);
        try {
            const response = await getNotes(elementtype, elementid, filterByNoteType ? noteType : null);
            setExistingNotes(response.data || []);
        } catch (error) {
            console.error("Error fetching notes:", error);
            setExistingNotes([]);
        } finally {
            setFetchingNotes(false);
        }
    };

    const formatIrelandTime = (timestamp) => {
        const date = new Date(timestamp);
        const options = {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: "Europe/Dublin",
        };
        return date.toLocaleString("en-IE", options);
    };

    const getTeacherName = (tid) => {
        const teacher = teachers.find((t) => t.id === tid);
        return teacher?.name || "Unknown";
    };

    const handleTeacherSelection = (teacherId) => {
        setSelectedTeachers((prev) =>
            prev.includes(teacherId)
                ? prev.filter((id) => id !== teacherId)
                : [...prev, teacherId]
        );
    };

    const handleSubmit = async () => {
        if (!note.trim()) {
            alert("Please provide a note before submitting.");
            return;
        }

        if (sendEmail && selectedTeachers.length === 0) {
            alert("Please select at least one teacher to notify.");
            return;
        }

        setLoading(true);

        try {
            const response = await createNote({
                elementtype,
                elementid,
                note_type: noteType,
                type: "general",
                note_text: note.trim(),
                teacherid,
            });

            const createdNote = response.data;

            if (sendEmail && createdNote?.id) {
                await sendNoteNotifications(selectedTeachers, createdNote.id);
            }

            setNoteAdded(true);
            setHasExistingNotes(true);
            onSuccess?.();
            setShow(false);
        } catch (error) {
            console.error("Error submitting note:", error);
            alert("Failed to save the note. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (note.trim()) {
            const confirmed = window.confirm(
                "You have unsaved changes. Are you sure you want to cancel? This will discard your note."
            );
            if (!confirmed) return;
        }
        setShow(false);
    };

    const handlePrint = () => {
        const notesHtml = existingNotes.map((n) => `
            <div style="margin-bottom:14px; padding:10px 12px; border-left:3px solid #1a56db; background:#f8f9fa; border-radius:4px;">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px;">
                    <span style="font-size:11px; color:#666;">${getTeacherName(n.teacherid)} &bull; ${formatIrelandTime(n.date_created)}</span>
                </div>
                <div style="font-size:13px; color:#333; white-space:pre-wrap;">${n.note_text}</div>
            </div>
        `).join('');

        // MOVED FROM ABOVE TO REMOVE NOTE TYPE FROM PRINTED NOTES
        // <span style="font-size:11px; font-weight:bold; background:#1a56db; color:white; padding:2px 8px; border-radius:4px;">${n.note_type}</span>

        const printWindow = window.open('', '_blank', 'width=800,height=600');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head><title>Notes — ${entityName}</title></head>
            <body style="font-family: Arial, sans-serif; padding: 32px; max-width: 760px; margin: 0 auto; color: #1f2937;">
                <h2 style="border-bottom: 2px solid #1a56db; padding-bottom: 8px; color: #1a56db; margin-bottom: 4px;">${entityName}</h2>
                <p style="color: #999; font-size: 11px; margin-bottom: 24px;">Printed: ${new Date().toLocaleString('en-IE', { timeZone: 'Europe/Dublin' })}</p>
                ${notesHtml || '<p style="color:#999;">No notes available.</p>'}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
    };

    const hasNotes = noteAdded || hasExistingNotes || existingNotes.length > 0;

    return (
        <>
            <button
                className="note-icon-button"
                onClick={() => setShow(true)}
                title={hasNotes ? "View/add notes" : "Add note"}
            >
                <img
                    src={process.env.PUBLIC_URL + (hasNotes
                        ? "/assets/imgs/file-pen-outline.svg"
                        : "/assets/imgs/file-outline.svg")}
                    alt="Notes"
                    style={{ width: 20, height: 20 }}
                />
            </button>

            <Modal show={show} onHide={handleCancel} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{getModalTitle()}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Existing Notes Accordion */}
                    {existingNotes.length > 0 && (
                        <Accordion className="mb-3">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>
                                    <strong>View Previous Notes ({existingNotes.length})</strong>
                                </Accordion.Header>
                                <Accordion.Body>
                                    {fetchingNotes ? (
                                        <p>Loading notes...</p>
                                    ) : (
                                        <div className="note-modal-notes-list">
                                            {existingNotes.map((n) => (
                                                <div key={n.id} className="note-modal-note-item mb-3">
                                                    <div className="note-modal-note-header">
                                                        {/* <span className="note-modal-note-type-badge">
                                                            {n.note_type}
                                                        </span> */}
                                                        <span className="note-modal-note-meta">
                                                            {getTeacherName(n.teacherid)} •{" "}
                                                            {formatIrelandTime(n.date_created)}
                                                        </span>
                                                    </div>
                                                    <div className="note-modal-note-content">{n.note_text}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}

                    {/* Note Input */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <strong>Leave a Note</strong>{" "}
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Enter your note here..."
                            required
                        />
                    </Form.Group>

                    {/* Email Notification Checkbox */}
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Send email notification"
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                        />
                    </Form.Group>

                    {/* Teacher Selection */}
                    {sendEmail && (
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <strong>Select teacher to notify:</strong>
                            </Form.Label>
                            {teachers.length === 0 ? (
                                <p className="text-muted">No teachers available for notification.</p>
                            ) : (
                                <div className="note-modal-teacher-selection">
                                    {teachers.map((teacher) => (
                                        <Form.Check
                                            key={teacher.id}
                                            type="checkbox"
                                            label={teacher.name}
                                            checked={selectedTeachers.includes(teacher.id)}
                                            onChange={() => handleTeacherSelection(teacher.id)}
                                        />
                                    ))}
                                </div>
                            )}
                        </Form.Group>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                        Cancel
                    </Button>
                    {/* {existingNotes.length > 0 && (
                        <Button variant="outline-secondary" onClick={handlePrint} disabled={loading}>
                            Print Notes
                        </Button>
                    )} */}
                    <button
                        className="note-modal-submit-button"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default NoteModal;